import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, ArrowLeft, TrendingDown, Zap, Info, CheckCircle2, Share2, Download, Copy, Check, Upload, TrendingUp, Pencil, Save, X, User } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import Navigation from "@/components/Navigation";
import PublicNavigation from "@/components/PublicNavigation";
import { useAuth } from "@/contexts/AuthContext";
import { ContactModal } from "@/components/ContactModal";
import { toast } from "sonner";
import axios from "axios";
import { generateOptimizationPDF } from "@/utils/pdfExport/optimization";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const OptimizationReportViewPage = () => {
  const { id } = useParams();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPublicView, setIsPublicView] = useState(true);
  const [copied, setCopied] = useState(false);
  const [uploadingCurve, setUploadingCurve] = useState(false);
  const [uploadingMonthlyCurve, setUploadingMonthlyCurve] = useState(false);
  const fileInputRef = useRef(null);
  const monthlyFileInputRef = useRef(null);
  const [editingComment, setEditingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  
  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    title: '',
    phone: '',
    email: '',
    address: ''
  });
  const [savingContact, setSavingContact] = useState(false);
  
  // State for conditions de validité checkboxes
  const [conditions, setConditions] = useState({
    climatisation: null, // null = non répondu, true = OUI, false = NON
    extension_activite: null,
    modification_equipements: null,
    augmentation_puissance: null,
    reduction_activite: null,
    renouvellement_equipements: null
  });

  useEffect(() => {
    loadReport();
  }, [id]);

  // Determine view mode based on authentication status
  // If user is authenticated, show admin controls; otherwise show public view
  useEffect(() => {
    if (!authLoading) {
      setIsPublicView(!user);
    }
  }, [user, authLoading]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/optimization-reports/${id}`);
      if (response.data.success) {
        const reportData = response.data.report;
        setReport(reportData);
        
        // Load existing contact info
        if (reportData.contact_info) {
          setContactInfo(prev => ({ ...prev, ...reportData.contact_info }));
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/reports/${report.share_token}/contact-info`,
        contactInfo,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setReport(prev => ({ ...prev, contact_info: response.data.contact_info }));
        setShowContactModal(false);
        toast.success('Contact mis à jour', {
          description: 'Les coordonnées seront utilisées dans le PDF.'
        });
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      toast.error('Erreur lors de la sauvegarde du contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleExport = async () => {
    if (!report) return;
    try {
      await generateOptimizationPDF(report);
      toast.success('PDF exporté', { description: 'Le rapport a été téléchargé.' });
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  const handleSaveComment = async () => {
    if (!report) return;
    setSavingComment(true);
    try {
      await axios.patch(
        `${API}/optimization-reports/${report.share_token}/comment`,
        { comment: commentDraft },
        { headers: getAuthHeader() }
      );
      setReport(prev => ({ ...prev, comment: commentDraft }));
      setEditingComment(false);
      toast.success('Commentaire sauvegardé');
    } catch (err) {
      console.error('Save comment error:', err);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingComment(false);
    }
  };
  
  const handleLoadCurveUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Format invalide', {
        description: 'Veuillez sélectionner un fichier CSV.',
      });
      return;
    }
    
    setUploadingCurve(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/optimization-reports/${report.share_token}/load-curve`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeader()
          },
        }
      );
      
      if (response.data.success) {
        toast.success('Courbe de charge importée', {
          description: `${response.data.data_points} points de données chargés.`,
        });
        
        // Reload report to get updated data
        loadReport();
      }
    } catch (error) {
      console.error('Error uploading load curve:', error);
      toast.error('Erreur d\'import', {
        description: error.response?.data?.detail || 'Impossible d\'importer le fichier CSV.',
      });
    } finally {
      setUploadingCurve(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleMonthlyLoadCurveUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Format invalide', {
        description: 'Veuillez sélectionner un fichier CSV.',
      });
      return;
    }
    
    setUploadingMonthlyCurve(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/optimization-reports/${report.share_token}/monthly-load-curve`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeader()
          },
        }
      );
      
      if (response.data.success) {
        toast.success('Courbe de charge annuelle importée', {
          description: `${response.data.data_points} points de données chargés.`,
        });
        
        // Reload report to get updated data
        loadReport();
      }
    } catch (error) {
      console.error('Error uploading annual load curve:', error);
      toast.error('Erreur d\'import', {
        description: error.response?.data?.detail || 'Impossible d\'importer le fichier CSV.',
      });
    } finally {
      setUploadingMonthlyCurve(false);
      if (monthlyFileInputRef.current) {
        monthlyFileInputRef.current.value = '';
      }
    }
  };
  
  const handleShare = async () => {
    const baseUrl = window.location.origin;
    const shareLink = `${baseUrl}/optimization/report/${report.share_token}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareLink);
        setCopied(true);
        toast.success('Lien copié', {
          description: 'Le lien du rapport a été copié dans le presse-papier.',
        });
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (clipboardError) {
      // Silently fall through to fallback method
    }
    
    // Fallback: Try execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success('Lien copié', {
          description: 'Le lien du rapport a été copié dans le presse-papier.',
        });
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error('execCommand failed');
      }
    } catch (fallbackError) {
      // Last resort: show the link for manual copying
      toast.info('Lien du rapport', {
        description: shareLink,
        duration: 10000,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
        {isPublicView ? <PublicNavigation reportType="optimization" showHomeLink={!!user} /> : <Navigation />}
        <div className="container mx-auto px-4 py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
        {isPublicView ? <PublicNavigation reportType="optimization" showHomeLink={!!user} /> : <Navigation />}
        <div className="container mx-auto px-4 py-12">
          <p className="text-destructive text-center">{error || 'Rapport non trouvé'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
      {isPublicView ? <PublicNavigation reportType="optimization" showHomeLink={!!user} /> : <Navigation />}
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {!isPublicView && (
            <Link to="/optimization/reports">
              <Button variant="ghost" className="mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux rapports
              </Button>
            </Link>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-6 mb-4">
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-2">{report.client_name}</h1>
                <div className="flex flex-wrap gap-4 text-muted-foreground">
                  <span>PDL : {report.pdl}</span>
                  {report.fta && <span>FTA : {report.fta}</span>}
                </div>
              </div>
              
              {!isPublicView && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="outline" onClick={() => setShowContactModal(true)} data-testid="contact-pdf-btn">
                    <User className="w-4 h-4 mr-2" />
                    Contact PDF
                  </Button>
                  <Button variant="outline" onClick={handleShare}>
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                    {copied ? 'Copié !' : 'Partager'}
                  </Button>
                  <Button onClick={handleExport}>
                    <Download className="w-4 h-4 mr-2" />
                    Exporter PDF
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
              <TabsTrigger value="costs">Analyse des coûts</TabsTrigger>
              <TabsTrigger value="details">Détails techniques</TabsTrigger>
              <TabsTrigger value="conditions">Conditions de validité</TabsTrigger>
            </TabsList>

            {/* VUE D'ENSEMBLE */}
            <TabsContent value="overview" className="space-y-6">
              {/* TURPE Explanation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    En quoi consiste les frais d'acheminement ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    Le <strong>TURPE</strong> (Tarif d'Utilisation des Réseaux Publics d'Électricité) est une composante 
                    essentielle de votre facture d'électricité. Il rémunère les gestionnaires de réseaux (RTE et Enedis) 
                    pour l'entretien et le développement des infrastructures électriques.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Ce tarif est déterminé par la Commission de Régulation de l'Énergie (CRE) et comprend :
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span><strong>Une part fixe</strong> liée à votre puissance souscrite en kVA</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span><strong>Une part variable</strong> calculée sur votre consommation en kWh</span>
                    </li>
                  </ul>
                  <div className="bg-accent/50 rounded-lg p-4 mt-4">
                    <p className="text-sm leading-relaxed">
                      💡 <strong>Bon à savoir :</strong> La part fixe du TURPE peut représenter jusqu'à 40% de vos coûts d'acheminement. 
                      En optimisant votre puissance souscrite, vous réduisez significativement cette part fixe.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Résumé de l'optimisation</CardTitle>
                  <CardDescription>Comparaison entre votre situation actuelle et optimisée</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-4 bg-accent/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Coût Actuel Annuel</p>
                      <p className="text-2xl font-bold">
                        {report.current_costs?.total > 0 ? `${report.current_costs.total.toFixed(2).replace('.', ',')} €` : 'N/A'}
                      </p>
                    </div>
                    <div className="p-4 bg-primary/10 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Coût Optimisé Annuel</p>
                      <p className="text-2xl font-bold text-primary">
                        {report.optimized_costs?.total > 0 ? `${report.optimized_costs.total.toFixed(2).replace('.', ',')} €` : 'N/A'}
                      </p>
                    </div>
                    <div className="p-4 bg-success/10 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Économie Annuelle</p>
                      <p className="text-2xl font-bold text-success">
                        {report.savings?.total > 0 ? `${report.savings.total.toFixed(2).replace('.', ',')} €` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Puissances */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Puissances Actuelles</CardTitle>
                    <CardDescription>Vos puissances souscrites actuellement</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.current_powers && Object.entries(report.current_powers).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center border-b pb-2">
                          <span className="text-sm font-medium uppercase">{key}</span>
                          <span className="text-lg font-semibold">{value} kVA</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      Puissances Proposées
                    </CardTitle>
                    <CardDescription>Puissances optimisées recommandées</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.proposed_powers && Object.entries(report.proposed_powers).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center border-b pb-2">
                          <span className="text-sm font-medium uppercase">{key}</span>
                          <span className="text-lg font-semibold text-primary">{value} kVA</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Commentaire du rapport */}
              {(report.comment || !isPublicView) && (
                <Card className="border-primary/20">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Info className="w-5 h-5 text-primary" />
                        Analyse de votre situation
                      </CardTitle>
                      {!isPublicView && !editingComment && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="edit-comment-btn"
                          onClick={() => { setCommentDraft(report.comment || ''); setEditingComment(true); }}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editingComment ? (
                      <div className="space-y-3">
                        <textarea
                          data-testid="comment-textarea"
                          className="w-full min-h-[160px] p-3 border rounded-lg text-sm leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
                          value={commentDraft}
                          onChange={(e) => setCommentDraft(e.target.value)}
                          placeholder="Saisissez votre commentaire..."
                        />
                        <div className="flex gap-2 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => setEditingComment(false)} disabled={savingComment}>
                            <X className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                          <Button size="sm" onClick={handleSaveComment} disabled={savingComment} data-testid="save-comment-btn">
                            {savingComment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                            Enregistrer
                          </Button>
                        </div>
                      </div>
                    ) : report.comment ? (
                      <div className="text-muted-foreground space-y-4">
                        {report.comment.split('\n\n').map((paragraph, index) => 
                          paragraph.trim() && (
                            <p key={index} className="leading-relaxed text-justify">
                              {paragraph.replace(/\n/g, ' ')}
                            </p>
                          )
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground italic">Aucun commentaire. Cliquez sur "Modifier" pour en ajouter un.</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Power Optimization Explanation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    A quoi correspond la puissance souscrite ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    La <strong>puissance souscrite</strong> correspond à la capacité maximale de consommation électrique 
                    que vous vous engagez à ne pas dépasser. Elle est exprimée en <strong>kVA</strong> (kilovoltampères) 
                    et détermine une grande partie de vos coûts d'acheminement.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-accent/30 rounded-lg">
                      <div className="w-8 h-8 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-destructive font-bold">⚠️</span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1 text-destructive">Le problème : Surcapacité coûteuse</h4>
                        <p className="text-sm text-muted-foreground">
                          Une puissance souscrite trop élevée vous fait payer pour une capacité que vous n'utilisez pas. 
                          Votre facture est gonflée artificiellement alors que votre consommation réelle est inférieure.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-success/10 rounded-lg">
                      <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-5 h-5 text-success" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1 text-success">La solution : Optimisation intelligente</h4>
                        <p className="text-sm text-muted-foreground">
                          En analysant votre courbe de charge sur 12 mois, nous identifions le niveau optimal de puissance 
                          qui correspond à vos besoins réels, tout en évitant les dépassements.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </TabsContent>

            {/* ANALYSE DES COÛTS */}
            <TabsContent value="costs" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Comparaison Détaillée des Coûts</CardTitle>
                  <CardDescription>Analyse des 7 composantes du coût d'acheminement (€/an)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold">Composante</th>
                          <th className="text-right py-3 px-4 font-semibold">Actuel</th>
                          <th className="text-right py-3 px-4 font-semibold text-primary">Optimisé</th>
                          <th className="text-right py-3 px-4 font-semibold text-success">Économie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.current_costs && report.optimized_costs && [
                          { key: 'comptage', label: 'Composante de comptage' },
                          { key: 'energie_reactive', label: 'Composante de l\'énergie réactive' },
                          { key: 'gestion', label: 'Composante de gestion' },
                          { key: 'depassements', label: 'Composante mensuelle de dépassements' },
                          { key: 'soutirage_fixe', label: 'Composante de soutirage (part fixe)' },
                          { key: 'soutirage_variable', label: 'Composante de soutirage (part variable)' },
                          { key: 'cta', label: 'Contribution Tarifaire d\'Acheminement' }
                        ].map(({ key, label }) => {
                          const current = report.current_costs[key] || 0;
                          const optimized = report.optimized_costs[key] || 0;
                          const saving = current - optimized;  // Calculate actual saving
                          const hasIncrease = saving < 0;  // True if cost increased
                          
                          return (
                            <tr key={key} className="border-b hover:bg-accent/50">
                              <td className="py-3 px-4">{label}</td>
                              <td className="text-right py-3 px-4">{current.toFixed(2)} €</td>
                              <td className={`text-right py-3 px-4 font-semibold ${hasIncrease ? 'text-destructive' : 'text-success'}`}>
                                {optimized.toFixed(2)} €
                              </td>
                              <td className={`text-right py-3 px-4 font-semibold ${hasIncrease ? 'text-destructive' : 'text-success'}`}>
                                {saving !== 0 ? `${saving > 0 ? '+' : ''}${saving.toFixed(2)} €` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="font-bold bg-accent/30">
                          <td className="py-3 px-4">TOTAL</td>
                          <td className="text-right py-3 px-4">{report.current_costs.total.toFixed(2)} €</td>
                          <td className={`text-right py-3 px-4 text-xl font-bold ${(report.current_costs.total - report.optimized_costs.total) >= 0 ? 'text-success' : 'text-destructive'}`}>
                            {report.optimized_costs.total.toFixed(2)} €
                          </td>
                          <td className={`text-right py-3 px-4 text-xl font-bold ${(report.current_costs.total - report.optimized_costs.total) >= 0 ? 'text-success' : 'text-destructive'}`}>
                            {((report.current_costs.total - report.optimized_costs.total) > 0 ? '+' : '')}{(report.current_costs.total - report.optimized_costs.total).toFixed(2)} €
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Courbe de charge annuelle */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        Votre courbe de charge annuelle
                      </CardTitle>
                      <CardDescription>
                        Visualisez les puissances atteintes sur l'année
                      </CardDescription>
                    </div>
                    {!isPublicView && (
                      <div>
                        <input
                          ref={monthlyFileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleMonthlyLoadCurveUpload}
                          style={{ display: 'none' }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => monthlyFileInputRef.current?.click()}
                          disabled={uploadingMonthlyCurve}
                        >
                          {uploadingMonthlyCurve ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Import en cours...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Importer CSV
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {report.annual_load_curve_data ? (
                    <div className="space-y-6">
                      {/* Statistics */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-primary/10 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Puissance moyenne</p>
                          <p className="text-xl font-bold text-primary">{report.annual_load_curve_data.stats.average.toLocaleString('fr-FR')} kW</p>
                        </div>
                        <div className="p-4 bg-destructive/10 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Puissance maximale</p>
                          <p className="text-xl font-bold text-destructive">{report.annual_load_curve_data.stats.max.toLocaleString('fr-FR')} kW</p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Puissance minimale</p>
                          <p className="text-xl font-bold text-blue-600">{report.annual_load_curve_data.stats.min.toLocaleString('fr-FR')} kW</p>
                        </div>
                      </div>

                      {/* Chart */}
                      <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={report.annual_load_curve_data.dates.map((date, index) => ({
                              date: new Date(date).getTime(),
                              power: report.annual_load_curve_data.values[index]
                            }))}
                            margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis 
                              dataKey="date"
                              type="number"
                              domain={['dataMin', 'dataMax']}
                              tickFormatter={(timestamp) => {
                                const date = new Date(timestamp);
                                return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                              }}
                              style={{ fontSize: '11px' }}
                            />
                            <YAxis 
                              label={{ value: 'kW', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                              style={{ fontSize: '11px' }}
                              domain={[0, (dataMax) => {
                                const maxPower = Math.max(
                                  ...[report.current_powers?.pointe, report.current_powers?.hph, report.current_powers?.hch, report.current_powers?.hpe, report.current_powers?.hce]
                                    .filter(v => v != null && !isNaN(v))
                                    .map(Number),
                                  0
                                );
                                return Math.max(dataMax, maxPower) * 1.05;
                              }]}
                            />
                            <Tooltip 
                              formatter={(value) => [`${value.toLocaleString('fr-FR')} kW`, 'Puissance']}
                              labelFormatter={(timestamp) => {
                                const date = new Date(timestamp);
                                return date.toLocaleDateString('fr-FR', { 
                                  day: 'numeric', 
                                  month: 'long', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                });
                              }}
                              contentStyle={{ fontSize: '12px' }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="power" 
                              stroke="#397DFF" 
                              strokeWidth={1}
                              dot={false}
                              isAnimationActive={false}
                            />
                            {/* Subscribed power reference line */}
                            {report.current_powers && (() => {
                              const maxPower = Math.max(
                                ...[report.current_powers.pointe, report.current_powers.hph, report.current_powers.hch, report.current_powers.hpe, report.current_powers.hce]
                                  .filter(v => v != null && !isNaN(v))
                                  .map(Number)
                              );
                              return maxPower > 0 ? (
                                <ReferenceLine
                                  y={maxPower}
                                  stroke="#DC3232"
                                  strokeWidth={1.5}
                                  strokeDasharray="6 3"
                                  label={{ value: `Puissance souscrite : ${maxPower} kVA`, position: 'insideTopRight', fill: '#DC3232', fontSize: 11, fontWeight: 'bold' }}
                                />
                              ) : null;
                            })()}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Period info */}
                      <div className="text-sm text-muted-foreground text-center">
                        Période : {report.annual_load_curve_data.stats.period} • {report.annual_load_curve_data.data_points.toLocaleString('fr-FR')} points de données
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-2">
                        Aucune donnée de courbe de charge annuelle disponible
                      </p>
                      {!isPublicView && (
                        <p className="text-sm text-muted-foreground">
                          Importez un fichier CSV contenant vos données de puissance pour visualiser votre courbe annuelle
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Explication des composantes */}
              <Card>
                <CardHeader>
                  <CardTitle>Comprendre les composantes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {(() => {
                      // Calculate if there are savings for each component
                      const hasSavings = (key) => {
                        const current = report.current_costs?.[key] || 0;
                        const optimized = report.optimized_costs?.[key] || 0;
                        return current > optimized && (current - optimized) > 0.01;
                      };
                      
                      const soutirageFixeSavings = hasSavings('soutirage_fixe');
                      const ctaSavings = hasSavings('cta');
                      const soutirageVariableSavings = hasSavings('soutirage_variable');
                      const comptageSavings = hasSavings('comptage');
                      const gestionSavings = hasSavings('gestion');
                      const reactifSavings = hasSavings('energie_reactive');
                      
                      return (
                        <>
                          <div className={`border-l-4 ${soutirageFixeSavings ? 'border-success' : 'border-muted'} pl-4 py-2`}>
                            <h4 className={`font-semibold mb-1 ${soutirageFixeSavings ? 'text-success' : ''}`}>
                              Composante de soutirage (part fixe){soutirageFixeSavings && ' - Principale économie'}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {soutirageFixeSavings 
                                ? "C'est la composante la plus impactée par l'optimisation. Elle dépend directement de votre puissance souscrite. En la réduisant, vous générez des économies immédiates et récurrentes."
                                : "Cette composante dépend de votre puissance souscrite. L'optimisation proposée ne génère pas d'économie significative sur cette composante."}
                            </p>
                          </div>
                          
                          <div className={`border-l-4 ${ctaSavings ? 'border-success' : 'border-muted'} pl-4 py-2`}>
                            <h4 className={`font-semibold mb-1 ${ctaSavings ? 'text-success' : ''}`}>
                              Contribution Tarifaire d'Acheminement (CTA)
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              La CTA finance les retraites des salariés des industries électriques et gazières. 
                              {ctaSavings 
                                ? " Elle est calculée proportionnellement à la part fixe du TURPE : moins de puissance = moins de CTA."
                                : " Elle reste identique car elle dépend de la part fixe du TURPE qui n'est pas modifiée."}
                            </p>
                          </div>
                          
                          <div className={`border-l-4 ${soutirageVariableSavings ? 'border-success' : 'border-muted'} pl-4 py-2`}>
                            <h4 className={`font-semibold mb-1 ${soutirageVariableSavings ? 'text-success' : ''}`}>
                              Composante de soutirage (part variable)
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Calculée sur votre consommation réelle en kWh. 
                              {soutirageVariableSavings
                                ? " L'optimisation permet de réduire cette composante."
                                : " Cette composante ne change pas avec l'optimisation de la puissance souscrite car elle dépend uniquement de votre consommation."}
                            </p>
                          </div>
                          
                          <div className={`border-l-4 ${comptageSavings ? 'border-success' : 'border-muted'} pl-4 py-2`}>
                            <h4 className={`font-semibold mb-1 ${comptageSavings ? 'text-success' : ''}`}>
                              Composante de comptage
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Rémunère la mise à disposition et la maintenance du compteur. 
                              {comptageSavings
                                ? " L'optimisation permet de réduire ce coût."
                                : " Ce coût reste identique car il dépend du type de compteur, pas de la puissance souscrite."}
                            </p>
                          </div>
                          
                          <div className={`border-l-4 ${gestionSavings ? 'border-success' : 'border-muted'} pl-4 py-2`}>
                            <h4 className={`font-semibold mb-1 ${gestionSavings ? 'text-success' : ''}`}>
                              Composante de gestion
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Couvre les frais de gestion administrative du contrat par Enedis. 
                              {gestionSavings
                                ? " L'optimisation permet de réduire ce montant."
                                : " Montant fixe qui ne varie pas avec l'optimisation de puissance."}
                            </p>
                          </div>
                          
                          <div className={`border-l-4 ${reactifSavings ? 'border-success' : 'border-muted'} pl-4 py-2`}>
                            <h4 className={`font-semibold mb-1 ${reactifSavings ? 'text-success' : ''}`}>
                              Composante de l'énergie réactive
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Facturation liée à la présence de puissance réactive sur votre installation. 
                              {reactifSavings
                                ? " L'optimisation permet de réduire cette composante."
                                : " L'optimisation de puissance n'agit pas sur cette composante."}
                            </p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* DÉTAILS TECHNIQUES */}
            <TabsContent value="details" className="space-y-6">
              {/* Explication des périodes tarifaires */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Les périodes tarifaires horosaisonnières
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Votre consommation est répartie selon 5 périodes tarifaires définies par Enedis. 
                    Cette répartition permet d'adapter les tarifs en fonction de la demande sur le réseau électrique national.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="bg-background/50 rounded p-3">
                      <strong className="text-primary">Pointe</strong> - Quelques heures par an, périodes de forte demande hivernale
                    </div>
                    <div className="bg-background/50 rounded p-3">
                      <strong className="text-primary">HPH</strong> - Heures Pleines Hiver (journées d'hiver)
                    </div>
                    <div className="bg-background/50 rounded p-3">
                      <strong className="text-primary">HCH</strong> - Heures Creuses Hiver (nuits d'hiver)
                    </div>
                    <div className="bg-background/50 rounded p-3">
                      <strong className="text-primary">HPE</strong> - Heures Pleines Été (journées d'été)
                    </div>
                    <div className="bg-background/50 rounded p-3">
                      <strong className="text-primary">HCE</strong> - Heures Creuses Été (nuits d'été)
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Consommations */}
              <Card>
                <CardHeader>
                  <CardTitle>Consommations Horosaisonnières</CardTitle>
                  <CardDescription>Répartition de votre consommation par période tarifaire (kWh)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {report.consumptions && Object.entries(report.consumptions).map(([key, value]) => (
                      <div key={key} className="p-4 bg-accent/50 rounded-lg text-center">
                        <p className="text-sm text-muted-foreground uppercase mb-1">{key}</p>
                        <p className="text-xl font-bold">{value > 0 ? value.toLocaleString() : 'N/A'} kWh</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Informations client */}
              <Card>
                <CardHeader>
                  <CardTitle>Informations du site</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {report.report_date && (
                    <div>
                      <p className="text-sm text-muted-foreground">Date du rapport</p>
                      <p className="font-medium">{report.report_date}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Adresse</p>
                    <p className="font-medium">{report.address?.street || 'N/A'}</p>
                    <p className="font-medium">{report.address?.postal_code} {report.address?.city}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Formule Tarifaire d'Acheminement</p>
                    <p className="font-medium">{report.fta || 'N/A'}</p>
                    {report.fta_description && (
                      <p className="text-sm text-muted-foreground mt-1">{report.fta_description}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            {/* Onglet Conditions de validité */}
            <TabsContent value="conditions" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Conditions de validité de l'optimisation
                  </CardTitle>
                  <CardDescription>
                    Pour garantir la pertinence de cette optimisation, veuillez répondre aux questions suivantes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    {/* Question 1 */}
                    <div className="border-b pb-4">
                      <p className="font-medium mb-3">Votre site dispose-t-il d'une climatisation ?</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setConditions({...conditions, climatisation: true})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.climatisation === true 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.climatisation === true ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.climatisation === true && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>OUI</span>
                        </button>
                        
                        <button
                          onClick={() => setConditions({...conditions, climatisation: false})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.climatisation === false 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.climatisation === false ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.climatisation === false && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>NON</span>
                        </button>
                      </div>
                    </div>

                    {/* Question 2 */}
                    <div className="border-b pb-4">
                      <p className="font-medium mb-3">Avez-vous un projet d'extension de votre activité sur les 12 prochains mois ?</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setConditions({...conditions, extension_activite: true})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.extension_activite === true 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.extension_activite === true ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.extension_activite === true && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>OUI</span>
                        </button>
                        
                        <button
                          onClick={() => setConditions({...conditions, extension_activite: false})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.extension_activite === false 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.extension_activite === false ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.extension_activite === false && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>NON</span>
                        </button>
                      </div>
                    </div>

                    {/* Question 3 */}
                    <div className="border-b pb-4">
                      <p className="font-medium mb-3">Prévoyez-vous une modification importante de vos équipements électriques ?</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setConditions({...conditions, modification_equipements: true})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.modification_equipements === true 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.modification_equipements === true ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.modification_equipements === true && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>OUI</span>
                        </button>
                        
                        <button
                          onClick={() => setConditions({...conditions, modification_equipements: false})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.modification_equipements === false 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.modification_equipements === false ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.modification_equipements === false && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>NON</span>
                        </button>
                      </div>
                    </div>

                    {/* Question 4 */}
                    <div className="border-b pb-4">
                      <p className="font-medium mb-3">Envisagez-vous une augmentation de votre puissance souscrite ?</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setConditions({...conditions, augmentation_puissance: true})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.augmentation_puissance === true 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.augmentation_puissance === true ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.augmentation_puissance === true && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>OUI</span>
                        </button>
                        
                        <button
                          onClick={() => setConditions({...conditions, augmentation_puissance: false})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.augmentation_puissance === false 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.augmentation_puissance === false ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.augmentation_puissance === false && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>NON</span>
                        </button>
                      </div>
                    </div>

                    {/* Question 5 */}
                    <div className="border-b pb-4">
                      <p className="font-medium mb-3">Prévoyez-vous une réduction de votre activité ?</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setConditions({...conditions, reduction_activite: true})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.reduction_activite === true 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.reduction_activite === true ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.reduction_activite === true && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>OUI</span>
                        </button>
                        
                        <button
                          onClick={() => setConditions({...conditions, reduction_activite: false})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.reduction_activite === false 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.reduction_activite === false ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.reduction_activite === false && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>NON</span>
                        </button>
                      </div>
                    </div>

                    {/* Question 6 */}
                    <div className="pb-4">
                      <p className="font-medium mb-3">Avez-vous prévu le renouvellement de certains équipements énergivores ?</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setConditions({...conditions, renouvellement_equipements: true})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.renouvellement_equipements === true 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.renouvellement_equipements === true ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.renouvellement_equipements === true && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>OUI</span>
                        </button>
                        
                        <button
                          onClick={() => setConditions({...conditions, renouvellement_equipements: false})}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            conditions.renouvellement_equipements === false 
                              ? 'border-primary bg-primary/10 text-primary font-semibold' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            conditions.renouvellement_equipements === false ? 'border-primary bg-primary' : 'border-gray-400'
                          }`}>
                            {conditions.renouvellement_equipements === false && (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span>NON</span>
                        </button>
                      </div>
                    </div>

                    {/* Note d'information */}
                    <Alert className="mt-4">
                      <Info className="w-4 h-4" />
                      <AlertDescription className="text-sm">
                        <strong>Important :</strong> Si vous avez répondu OUI à l'une de ces questions, 
                        nous vous recommandons de contacter votre chargé d'affaires avant de valider cette optimisation.
                      </AlertDescription>
                    </Alert>
                  </div>
                </CardContent>
              </Card>

              {/* Conclusion */}
              {report.conclusion && (
                <Card className="border-success/20 bg-success/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-success" />
                      Conclusion
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-muted-foreground space-y-4">
                      {report.conclusion.split('\n\n').map((paragraph, index) => 
                        paragraph.trim() && (
                          <p key={index} className="leading-relaxed text-justify">
                            {paragraph.replace(/\n/g, ' ')}
                          </p>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* Contact Info Modal */}
      <ContactModal
        open={showContactModal}
        onOpenChange={setShowContactModal}
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        onSave={handleSaveContact}
        saving={savingContact}
      />
    </div>
  );
};

export default OptimizationReportViewPage;
